import React, { useState, useRef, useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { AttachmentIcon, ArrowUpIcon, ArrowUpDownIcon, SmallCloseIcon } from '@chakra-ui/icons';
// Removed assistant-ui slash command dependencies to make the input provider-agnostic
import { useTypingAnalytics, usePostHogAnalytics } from '../../hooks/usePostHogAnalytics';
import { PostHogEvents } from '../../utils/posthogEvents';
import styles from './ChatInputBar.module.css';

interface ChatInputState {
  text: string;
  attachments: File[];
  isExpanded: boolean;
  isFullScreen: boolean;
  isFullScreenExiting: boolean;
  isScrollable: boolean;
  errors: ValidationError[];
  suggestions: Suggestion[];
  activeTools: string[];
}

interface ValidationError {
  type: 'spelling' | 'grammar' | 'length';
  message: string;
  position: number;
}

interface Suggestion {
  type: 'correction' | 'completion';
  text: string;
  position: number;
}

interface ChatInputBarProps {
  onSubmit: (message: string, attachments?: File[]) => void;
  placeholder?: string;
  // Optional soft limit for UX only. If omitted, no client cap.
  maxLength?: number;
  enableTools?: boolean;
  enableVoice?: boolean;
  disabled?: boolean;
  // New: when true, keep typing enabled but block sending
  sendDisabled?: boolean;
  isEmptyState?: boolean;
  isCentered?: boolean;
  onFullScreenChange?: (isFullScreen: boolean) => void;
  // Optional style override for the outer container (used for compact/embedded placements)
  containerStyle?: React.CSSProperties;
}

const ChatInputBar: React.FC<ChatInputBarProps> = ({
  onSubmit,
  placeholder = "Ask me anything...",
  // Remove hard cap by default; when undefined, we accept any length
  maxLength,
  enableTools = true,
  enableVoice = false,
  disabled = false,
  sendDisabled = false,
  isEmptyState = false,
  isCentered = false,
  onFullScreenChange,
  containerStyle
}) => {
  const [inputState, setInputState] = useState<ChatInputState>({
    text: '',
    attachments: [],
    isExpanded: false,
    isFullScreen: false,
    isFullScreenExiting: false,
    isScrollable: false,
    errors: [],
    suggestions: [],
    activeTools: []
  });

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const outerContainerRef = useRef<HTMLDivElement>(null);

  // Slash commands temporarily disabled (provider-agnostic input)

  // PostHog analytics hooks
  const { startTyping, endTyping, updateCharacterCount } = useTypingAnalytics();
  const DEBUG_CHAT_LOGS: boolean = (() => {
    try {
      const v = (import.meta as any)?.env?.VITE_ENABLE_CHAT_DEBUG;
      if (typeof v === 'string') {
        const s = v.toLowerCase();
        return s === '1' || s === 'true' || s === 'yes';
      }
    } catch {}
    return false;
  })();
  const { trackLearning, trackEngagement } = usePostHogAnalytics();

  // Auto-resize textarea - expand from bottom only, max 8 lines, or full screen
  const adjustTextareaHeight = useCallback((forceReset = false) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    if (inputState.isFullScreen && !forceReset) {
      // Full screen mode - remove inline height to let CSS handle it
      textarea.style.height = '';
      textarea.style.overflowY = 'auto';
      return;
    }

    // Always reset height first
    textarea.style.height = 'auto';
    textarea.style.overflowY = 'hidden';
    
    // Force a reflow
    void textarea.offsetHeight;
    
    const scrollHeight = textarea.scrollHeight;
    const lineHeight = 24; // Approximate line height in pixels
    const maxLines = 8;
    const maxHeight = lineHeight * maxLines; // 8 lines maximum
    const isScrollable = scrollHeight > maxHeight;
    
    if (isScrollable) {
      // Lock at 8 lines and enable scrolling
      textarea.style.height = `${maxHeight}px`;
      textarea.style.overflowY = 'auto';
    } else {
      // Allow expansion up to 8 lines
      textarea.style.height = `${scrollHeight}px`;
      textarea.style.overflowY = 'hidden';
    }

    const nextIsExpanded = scrollHeight > lineHeight * 1.5;
    const nextIsScrollable = isScrollable;
    setInputState(prev => {
      if (prev.isExpanded === nextIsExpanded && prev.isScrollable === nextIsScrollable) {
        return prev;
      }
      return {
        ...prev,
        isExpanded: nextIsExpanded,
        isScrollable: nextIsScrollable,
      };
    });
  }, [inputState.isFullScreen]);

  // Handle text change
  const handleTextChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newText = e.target.value;
    const prevText = inputState.text;
    
    // Track typing start/end
    if (newText.length > 0 && prevText.length === 0) {
      startTyping();
    } else if (newText.length === 0 && prevText.length > 0) {
      endTyping();
    }
    
    // Update character count for analytics
    updateCharacterCount(newText.length);
    
    // Accept all text; no hard cap. If maxLength is provided, it's treated as a soft indicator only.
    setInputState(prev => ({
      ...prev,
      text: newText,
      errors: [], // Clear errors on change
      suggestions: []
    }));
    
    requestAnimationFrame(() => {
      adjustTextareaHeight();
    });
  }, [maxLength, adjustTextareaHeight, inputState.text, startTyping, endTyping, updateCharacterCount]);

  // Handle full screen toggle with animation (defined before handleKeyDown to avoid TDZ)
  const handleFullScreenToggle = useCallback(() => {
    if (inputState.isFullScreen) {
      // Track full-screen mode exit
      trackEngagement(PostHogEvents.FEATURE_EXPLORED, {
        feature_name: 'fullscreen_chat_exit',
        feature_area: 'chat_interface'
      });

      // Start exit animation and reset scrollable immediately
      setInputState(prev => ({
        ...prev,
        isFullScreenExiting: true,
        isScrollable: false // Reset immediately
      }));
      // After animation, actually close
      setTimeout(() => {
        setInputState(prev => ({
          ...prev,
          isFullScreen: false,
          isFullScreenExiting: false
        }));
        // Solution 5: Use forceReset parameter
        requestAnimationFrame(() => {
          if (textareaRef.current) {
            adjustTextareaHeight(true); // Force reset
          }
        });
      }, 200); // Match animation duration
    } else {
      // Track full-screen mode entry
      trackEngagement(PostHogEvents.FEATURE_EXPLORED, {
        feature_name: 'fullscreen_chat_enter',
        feature_area: 'chat_interface'
      });
      
      // Open full screen
      setInputState(prev => ({
        ...prev,
        isFullScreen: true,
        isFullScreenExiting: false
      }));
    }
  }, [inputState.isFullScreen, adjustTextareaHeight, trackEngagement]);

  // Handle form submission
  const handleSubmit = useCallback((e?: React.FormEvent) => {
    if (e) e.preventDefault();
    
    const trimmedText = inputState.text.trim();
    const hasAttachments = inputState.attachments.length > 0;
    if (DEBUG_CHAT_LOGS) console.log('🔄 ChatInputBar handleSubmit:', {
      hasText: !!trimmedText,
      hasAttachments,
      disabled,
      sendDisabled,
      willSubmit: !!(((trimmedText && !disabled && !sendDisabled) || (hasAttachments && !disabled && !sendDisabled))),
      eventType: e?.type || 'manual',
      timestamp: new Date().toISOString()
    });
    
    if ((trimmedText.length === 0 && !hasAttachments) || disabled || sendDisabled) {
      console.log('⛔ ChatInputBar submit blocked:', { hasText: !!trimmedText, hasAttachments, disabled });
      return;
    }

    // End typing session before submitting
    if (trimmedText.length > 0) {
      endTyping();
    }

    // Track message submission with detailed analytics
    if (trimmedText) {
      const wordCount = trimmedText.split(/\s+/).filter(word => word.length > 0).length;
      const messageType = trimmedText.includes('?') ? 'question' : 
                         trimmedText.toLowerCase().includes('please') || trimmedText.toLowerCase().includes('can you') ? 'request' : 
                         'statement';

      trackLearning(PostHogEvents.CHAT_MESSAGE_SENT, {
        word_count: wordCount,
        character_count: trimmedText.length,
        message_type: messageType,
        has_attachments: hasAttachments,
        attachment_count: inputState.attachments.length,
        input_method: e?.type === 'keydown' ? 'keyboard' : 'button',
        feature_area: 'chat_interaction'
      });
    }

    // Track attachment usage if present
    if (hasAttachments) {
      trackEngagement(PostHogEvents.FEATURE_EXPLORED, {
        feature_name: 'file_attachment',
        interaction_count: inputState.attachments.length,
        feature_area: 'chat_interaction'
      });
    }

    // Submit the message - ChatPage handles duplicate prevention
    onSubmit(trimmedText, hasAttachments ? inputState.attachments : undefined);
    
    // Clear the input after submission
    if (DEBUG_CHAT_LOGS) console.log('🧹 ChatInputBar clearing input state after submit');
    
    // Clear the input
    setInputState(prev => ({
      ...prev,
      text: '',
      attachments: [],
      isExpanded: false,
      isFullScreen: false,
      isFullScreenExiting: false,
      isScrollable: false,
      errors: [],
      suggestions: []
    }));
    
    if (DEBUG_CHAT_LOGS) console.log('✅ ChatInputBar input state cleared, should be re-enabled now');

    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  }, [inputState.text, inputState.attachments, disabled, onSubmit, endTyping, trackLearning, trackEngagement]);

  // Handle key events
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Submit on Enter (without Shift)
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!sendDisabled) {
        handleSubmit();
      } else {
        // Ignore submit while send is disabled; allow typing
      }
      return;
    }

    // Handle Escape key
    if (e.key === 'Escape') {
      if (inputState.isFullScreen) {
        handleFullScreenToggle();
      } else {
        setInputState(prev => ({
          ...prev,
          text: '',
          errors: [],
          suggestions: []
        }));
        if (textareaRef.current) {
          textareaRef.current.style.height = 'auto';
        }
      }
    }
  }, [handleSubmit, inputState.isFullScreen, handleFullScreenToggle]);

  // Handle file selection
  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setInputState(prev => ({
      ...prev,
      attachments: [...prev.attachments, ...files]
    }));
  }, []);

  // Handle drag and drop
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    setInputState(prev => ({
      ...prev,
      attachments: [...prev.attachments, ...files]
    }));
  }, []);

  // Remove attachment
  const removeAttachment = useCallback((index: number) => {
    setInputState(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  }, []);

  // Focus textarea on container click
  const handleContainerClick = useCallback(() => {
    textareaRef.current?.focus();
  }, []);

  // Handle clicking outside full screen to close
  const handleFullScreenBackdropClick = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      setInputState(prev => ({
        ...prev,
        isFullScreen: false,
        isFullScreenExiting: true
      }));
      // After animation, actually close
      setTimeout(() => {
        setInputState(prev => ({
          ...prev,
          isFullScreen: false,
          isFullScreenExiting: false
        }));
      }, 200); // Match animation duration
    }
  }, []);

  // Notify parent when fullscreen state changes
  useEffect(() => {
    onFullScreenChange?.(inputState.isFullScreen);
    
    // Force a layout recalculation when exiting fullscreen
    if (!inputState.isFullScreen) {
      setTimeout(() => {
        if (outerContainerRef.current) {
          // Force the browser to recalculate layout
          const container = outerContainerRef.current;
          container.style.display = 'none';
          void container.offsetHeight; // Force reflow
          container.style.display = '';
          
          // Also trigger parent container reflow
          const parent = container.parentElement;
          if (parent) {
            parent.style.display = 'none';
            void parent.offsetHeight;
            parent.style.display = '';
          }
        }
      }, 250); // Wait for exit animation to complete
    }
  }, [inputState.isFullScreen, onFullScreenChange]);

  // Solution 4 & 5: Force complete reset when exiting fullscreen
  useEffect(() => {
    if (!inputState.isFullScreen && !inputState.isFullScreenExiting && textareaRef.current) {
      // Double-check reset after transition
      adjustTextareaHeight(true);
    }
  }, [inputState.isFullScreen, inputState.isFullScreenExiting, adjustTextareaHeight]);

  // Handle typing timeout - end typing after 3 seconds of inactivity
  useEffect(() => {
    if (inputState.text.length === 0) return;

    const timeout = setTimeout(() => {
      endTyping();
    }, 3000); // End typing after 3 seconds of inactivity

    return () => clearTimeout(timeout);
  }, [inputState.text, endTyping]);

  useEffect(() => {
    adjustTextareaHeight();
  }, [inputState.isFullScreen]);

  // Fullscreen centers to viewport; no sidebar offset tracking needed

  const isSubmitDisabled = (!inputState.text.trim() && inputState.attachments.length === 0) || disabled;
  const isSendBlocked = sendDisabled || isSubmitDisabled;
  const characterCount = inputState.text.length;
  const hasSoftLimit = typeof maxLength === 'number' && Number.isFinite(maxLength);
  const isNearLimit = hasSoftLimit ? characterCount > (maxLength as number) * 0.8 : false;
  
  // Debug disabled state changes
  useEffect(() => {
    // Intentionally left blank to disable verbose console logging in production
  }, [disabled, inputState.text, isSubmitDisabled, placeholder]);

  // Render full screen mode - just enlarged text box
  if (inputState.isFullScreen) {
    return ReactDOM.createPortal(
      <div 
        className={`${styles.fullScreenContainer} ${inputState.isFullScreenExiting ? styles.fullScreenExiting : ''}`}
      >
        <div className={styles.fullScreenWrapper}>
          <div className={styles.fullScreenInner}>
            {/* Expand Button - Top Right */}
            <button
              type="button"
              className={styles.expandButton}
              onClick={handleFullScreenToggle}
              title={inputState.isFullScreen ? 'Exit full-screen' : 'Enter full-screen'}
              disabled={disabled}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M2 10 L6 10 L6 14" />
                <path d="M10 2 L10 6 L14 6" />
              </svg>
            </button>

            <div className={styles.fullScreenContent}>
            {/* Textarea takes most space */}
            <textarea
              ref={textareaRef}
              value={inputState.text}
              onChange={handleTextChange}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              className={styles.textarea}
              disabled={disabled}
              rows={1}
            />
            
            {/* Button row at bottom */}
            <div className={styles.fullScreenButtonRow}>
              {/* Attachment Button */}
              <button
                type="button"
                className={styles.attachButton}
                onClick={() => fileInputRef.current?.click()}
                title="Attach file"
                disabled={disabled}
              >
                <AttachmentIcon />
              </button>

              {/* Submit Button */}
              <button
                type="button"
                className={`${styles.submitButton} ${isSendBlocked ? styles.submitDisabled : ''}`}
                onClick={handleSubmit}
                disabled={isSendBlocked}
                title="Send message"
              >
                <ArrowUpIcon />
              </button>
            </div>
          </div>
          </div>
        </div>

        {/* Hidden File Input */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={handleFileSelect}
          className={styles.hiddenFileInput}
          accept=".pdf,.doc,.docx,.txt,.md,.png,.jpg,.jpeg,.gif"
        />

        {/* Slash Command Dropdown removed in provider-agnostic mode */}
      </div>,
      document.body
    );
  }

  // Normal mode rendering
  return (
    <div 
      ref={outerContainerRef}
      className={`${styles.outerContainer} ${isCentered ? styles.centeredInput : ''} ${isEmptyState ? styles.emptyStateInput : ''}`}
      style={containerStyle}
    >
      {/* Attachments Preview */}
      {inputState.attachments.length > 0 && (
        <div className={styles.attachmentsPreview}>
          {inputState.attachments.map((file, index) => (
            <div key={index} className={styles.attachmentItem}>
              <span className={styles.fileName}>{file.name}</span>
              <button
                type="button"
                onClick={() => removeAttachment(index)}
                className={styles.removeAttachment}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Main Input Area - Original Structure */}
      <div 
        className={`${styles.inputWrapper} ${inputState.isExpanded ? styles.expanded : ''} ${disabled ? styles.disabled : ''}`}
        onClick={handleContainerClick}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        ref={containerRef}
      >
        {/* Expand Button - Top Right - Only show when scrollable */}
        {inputState.isScrollable && (
          <button
            type="button"
            className={styles.expandButton}
            onClick={handleFullScreenToggle}
            title="Expand to full screen"
            disabled={disabled}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M10 2 L14 2 L14 6" />
              <path d="M6 14 L2 14 L2 10" />
            </svg>
          </button>
        )}
        <div className={styles.inputContent}>
          {/* Textarea */}
          <textarea
            ref={textareaRef}
            value={inputState.text}
            onChange={handleTextChange}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className={styles.textarea}
            disabled={disabled}
            rows={1}
          />

          {/* Button row */}
          <div className={styles.buttonRow}>
            {/* Attachment Button */}
            <button
              type="button"
              className={styles.attachButton}
              onClick={() => fileInputRef.current?.click()}
              title="Attach file"
              disabled={disabled}
            >
              <AttachmentIcon />
            </button>

            {/* Submit Button */}
            <button
              type="button"
              className={`${styles.submitButton} ${isSendBlocked ? styles.submitDisabled : ''}`}
              onClick={handleSubmit}
              disabled={isSendBlocked}
              title="Send message"
            >
              <ArrowUpIcon />
            </button>
          </div>
        </div>

        {/* Character Count (shown only when a soft limit is provided) */}
        {hasSoftLimit && (isNearLimit || characterCount >= (maxLength as number)) && (
          <div className={`${styles.characterCount} ${characterCount >= maxLength ? styles.overLimit : ''}`}>
            {characterCount} / {maxLength}
          </div>
        )}

        {/* Error Indicators */}
        {inputState.errors.length > 0 && (
          <div className={styles.errorIndicators}>
            {inputState.errors.map((error, index) => (
              <span key={index} className={styles.errorIndicator} title={error.message}>
                {error.type === 'spelling' ? '📝' : error.type === 'grammar' ? '📖' : '⚠️'}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        onChange={handleFileSelect}
        className={styles.hiddenFileInput}
        accept=".pdf,.doc,.docx,.txt,.md,.png,.jpg,.jpeg,.gif"
      />

      {/* Slash Command Dropdown removed in provider-agnostic mode */}
    </div>
  );
};

export default ChatInputBar;
